import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { verifyInviteToken, acceptInvite } from '../services/inviteService';
import { assetUrl } from '../utils/assets';
import './SetPassword.css';
import './Login.css';

const FIREBASE_ERROR_MESSAGES = {
  'auth/email-already-in-use':
    'This email is already registered. If you have an account, sign in on the login page instead. If you were invited again, contact your administrator.',
  'auth/weak-password': 'Password is too weak. Please choose a longer or stronger password.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'Account creation is not allowed. Please contact your administrator.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
};

function getFriendlyErrorMessage(err) {
  if (err.details?.message) return err.details.message;
  const code = err.code || (err.message && err.message.match(/\(([^)]+)\)/)?.[1]);
  if (code && FIREBASE_ERROR_MESSAGES[code]) return FIREBASE_ERROR_MESSAGES[code];
  return err.message || 'Failed to set password. Please try again.';
}

const SetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [preferReducedMotion, setPreferReducedMotion] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Set password · Compliance Dashboard';
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPreferReducedMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        setTokenInvalid(true);
        setVerifying(false);
        return;
      }
      try {
        const data = await verifyInviteToken(token);
        if (!cancelled && data.valid && data.email) {
          setEmail(data.email);
        } else if (!cancelled) {
          setTokenInvalid(true);
        }
      } catch (err) {
        if (!cancelled) {
          setTokenInvalid(true);
          setError(err.message || 'Invalid or expired invite link.');
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await acceptInvite(token, password);
      navigate('/?invite=success', { replace: true });
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = assetUrl('logo.png');
  const videoSrc = assetUrl('grey.mp4');
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const shell = (children) => (
    <div className="set-password-page">
      <div className="login-page-logo" aria-hidden="true">
        <img src={logoSrc} alt="" width={96} height={96} />
      </div>
      <div className="set-password-visual">
        {preferReducedMotion ? (
          <div className="set-password-video login-video--static" aria-hidden="true" />
        ) : (
          <video
            className="set-password-video"
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          />
        )}
      </div>
      <div className="set-password-container">{children}</div>
    </div>
  );

  if (verifying) {
    return shell(
      <div className="set-password-card">
        <p className="set-password-verifying" role="status" aria-live="polite">
          Verifying invite link…
        </p>
      </div>
    );
  }

  if (tokenInvalid) {
    return shell(
      <div className="set-password-card">
        <h1 className="set-password-title">Invalid or expired link</h1>
        <p className="set-password-message">
          This invite link is invalid or has already been used. Please request a new invite or
          contact your administrator.
        </p>
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        <Link to="/" className="set-password-link">
          Return to login
        </Link>
      </div>
    );
  }

  return shell(
    <div className="set-password-card">
      <div className="set-password-header">
        <h1>Set your password</h1>
        <p>
          You’re signing up as <strong>{email}</strong>. Choose a password to complete setup.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="set-password-form" aria-busy={loading}>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            aria-describedby="password-hint"
          />
          <p id="password-hint" className="form-hint">
            Must be at least 6 characters.
          </p>
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            placeholder="Repeat password"
            autoComplete="new-password"
            aria-invalid={passwordsMismatch}
          />
          {passwordsMismatch && (
            <p className="form-hint form-hint--error" role="status">
              Passwords do not match yet.
            </p>
          )}
        </div>
        <button
          type="button"
          className="ui-btn ui-btn--ghost"
          onClick={() => setShowPassword((v) => !v)}
          aria-pressed={showPassword}
        >
          {showPassword ? 'Hide passwords' : 'Show passwords'}
        </button>
        {error && (
          <div className="set-password-error error-message" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="login-button" disabled={loading || passwordsMismatch}>
          {loading ? 'Setting password…' : 'Set password'}
        </button>
      </form>
    </div>
  );
};

export default SetPassword;
